import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  designType,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";

// Sideways scroll of the sticky header, driven by the body scroller's own
// scroll position on the compositor (CSS scroll-driven animation) so header
// and body columns can never drift apart. --wh-range is the body's
// scrollable width, refreshed on resize/data change only.
const whHeadShift = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(calc(var(--wh-range, 0px) * -1)); }
`;
const SUPPORTS_SCROLL_TIMELINE =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("animation-timeline: scroll()");
const scrollDrivenHeadCellSx = SUPPORTS_SCROLL_TIMELINE
  ? {
      animation: `${whHeadShift} auto linear both`,
      animationTimeline: "--whx",
    }
  : {};

export const WorkingHoursReport = ({
  startDate,
  endDate,
  setWorkingHoursDetails,
  setMasterLeaveDataForSearch,
  masterLeaveDataForSearch,
  workingHoursDetails,
  filteredWorkforceData,
  fetchDetails,
  setFetchDetails,
  departmentId,
  rowsPerPage,
  setRowsPerPage,
  page,
  setPage,
  hoursViewType,
  header1,
  header2,
  customFilterHeight = 0,
}) => {
  const { domain, token, topbarCompanyId, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );

  const [isLoading, setIsLoading] = useState(false);
  // The table grows with its data (the page scrolls vertically, like every
  // other report) while horizontal scroll stays INSIDE the table. The header
  // is its own table in a natively position:sticky wrapper (pins to the page,
  // no JS, no flicker) and its scrollLeft is mirrored from the body scroller.
  const headScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);
  // Sticky "proxy" horizontal scrollbar: the body's own scrollbar sits at the
  // very end of a long table, so it's hidden and this one — pinned just above
  // the footer bar — mirrors it, letting the user scroll sideways from any
  // vertical position.
  const proxyScrollRef = useRef(null);
  const proxyInnerRef = useRef(null);
  const footerRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(56);
  const dynamicHeaders = workingHoursDetails?.headers || [];
  const tableRows = filteredWorkforceData || [];
  const visibleSummaryHeaders = header2;
  const isCompactHoursView =
    hoursViewType === "working_hours" || hoursViewType === "ot_hours";
  // Widened from the original 104/140/140 — that width fit maybe 8-10
  // characters, so anything like a real employee id ("buggyl2024...")
  // truncated on nearly every row. Sized to fit typical values on one line;
  // anything still longer than this now wraps to a second line (see the
  // whiteSpace:"normal" cells below) rather than being clipped.
  const employeeIdColumnWidth = isCompactHoursView ? 150 : 160;
  const employeeNameColumnWidth = isCompactHoursView ? 170 : 190;
  const departmentColumnWidth = isCompactHoursView ? 150 : 170;
  const dynamicColumnWidth = isCompactHoursView ? 96 : 104;
  const summaryColumnWidth = isCompactHoursView ? 100 : 108;
  const getEmployeeColumnWidth = (headerKey) => {
    switch (headerKey) {
      case "employee_id":
        return employeeIdColumnWidth;
      case "user_name":
        return employeeNameColumnWidth;
      case "department_name":
        return departmentColumnWidth;
      default:
        return employeeNameColumnWidth;
    }
  };
  // Computed from header1's actual (save-view-driven) order/subset rather
  // than fixed keys, so the frozen columns stay correctly positioned no
  // matter which identity columns are visible or how they're ordered.
  const header1LeftOffsets = useMemo(() => {
    let runningLeft = 0;
    const offsets = {};
    header1.forEach((header) => {
      offsets[header.key] = runningLeft;
      runningLeft += getEmployeeColumnWidth(header.key);
    });
    return offsets;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header1, employeeIdColumnWidth, employeeNameColumnWidth, departmentColumnWidth]);

  // Same header lines for every date range: the name on one line, plus the
  // sub-header (e.g. "( Duty + OT )") on a second line when a column has one.
  // Each word of the name gets its own line ("ABS" / "Days", "Duty" /
  // "Hours") so the summary columns can stay narrow.
  const getSummaryHeaderLines = (header) => {
    const words = String(header?.name || "").trim().split(" ").filter(Boolean);
    const lines = words.length ? words : [""];
    return header?.subHeader ? [...lines, header.subHeader] : lines;
  };

  useEffect(() => {
    const fetchUsertaskDetails = async () => {
      try {
        setIsLoading(true);
        let searchStartDate = startDate.split("/").reverse().join("-");
        let searchEndDate = endDate.split("/").reverse().join("-");
        const response = await fetch(
          `${domain}tabular-report/?start_date=${searchStartDate}&end_date=${searchEndDate}&department_id=${departmentId}&company_id=${topbarCompanyId}&user_id=${userDetails?.id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          setWorkingHoursDetails(res);
          setMasterLeaveDataForSearch(res);
        } else if (response.status === 409) {
          setWorkingHoursDetails([]);
          setMasterLeaveDataForSearch([]);
          sessionHandling();
        } else {
          setWorkingHoursDetails([]);
          setMasterLeaveDataForSearch([]);
          throw new Error(res.error);
        }
      } catch (error) {
        setWorkingHoursDetails([]);
        setMasterLeaveDataForSearch([]);
        showToast("error", error.message);
      } finally {
        setIsLoading(false);
      }
    };
    if (fetchDetails) {
      fetchUsertaskDetails();
      setFetchDetails(false);
    }
  }, [
    domain,
    token,
    startDate,
    topbarCompanyId,
    endDate,
    fetchDetails,
    setWorkingHoursDetails,
    setMasterLeaveDataForSearch,
    setFetchDetails,
    departmentId,
    userDetails?.id,
  ]);

  // Keeps the sticky header's columns locked to the body's. Preferred path:
  // scroll-driven CSS animation (see whHeadShift) — this effect then only
  // publishes the scrollable range. Fallback (no scroll-timeline support):
  // mirror scrollLeft from a native passive scroll listener.
  useEffect(() => {
    const body = bodyScrollRef.current;
    const head = headScrollRef.current;
    if (!body || !head) return undefined;
    const publishRange = () => {
      head.style.setProperty(
        "--wh-range",
        `${Math.max(0, body.scrollWidth - body.clientWidth)}px`,
      );
      const proxy = proxyScrollRef.current;
      const proxyInner = proxyInnerRef.current;
      if (proxy && proxyInner) {
        proxyInner.style.width = `${body.scrollWidth}px`;
        proxy.style.display =
          body.scrollWidth > body.clientWidth + 1 ? "block" : "none";
      }
      const footer = footerRef.current;
      if (footer) setFooterHeight(footer.offsetHeight || 56);
    };
    publishRange();
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(publishRange);
      observer.observe(body);
      if (body.firstElementChild) observer.observe(body.firstElementChild);
      // Re-measure whenever the footer bar itself changes height.
      if (footerRef.current) observer.observe(footerRef.current);
    }
    window.addEventListener("resize", publishRange);
    const proxy = proxyScrollRef.current;
    // One side DRIVES at a time. Scroll events arrive a frame or more after
    // the scroll that caused them, so during a drag the other side's echo
    // events carry OLD positions; letting them write back is what snapped
    // the table (or the pinned bar) back to where it had been. The side that
    // scrolls first becomes the driver and the other side's events are
    // ignored until the driver has been idle for a moment.
    let driver = null;
    let idleTimer = null;
    const drive = (who, from, to) => {
      if (driver && driver !== who) return;
      driver = who;
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => {
        driver = null;
      }, 150);
      if (Math.abs(to.scrollLeft - from.scrollLeft) >= 1) {
        to.scrollLeft = from.scrollLeft;
      }
    };
    const onBodyScroll = () => {
      if (proxy) drive("body", body, proxy);
    };
    const onProxyScroll = () => {
      drive("proxy", proxy, body);
    };
    body.addEventListener("scroll", onBodyScroll, { passive: true });
    if (proxy) proxy.addEventListener("scroll", onProxyScroll, { passive: true });
    let sync = null;
    if (!SUPPORTS_SCROLL_TIMELINE) {
      sync = () => {
        head.scrollLeft = body.scrollLeft;
      };
      sync();
      body.addEventListener("scroll", sync, { passive: true });
    }
    return () => {
      window.removeEventListener("resize", publishRange);
      if (observer) observer.disconnect();
      if (sync) body.removeEventListener("scroll", sync);
      window.clearTimeout(idleTimer);
      body.removeEventListener("scroll", onBodyScroll);
      if (proxy) proxy.removeEventListener("scroll", onProxyScroll);
    };
  }, [isLoading, tableRows.length, dynamicHeaders.length]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (topbarCompanyId) {
      setFetchDetails(true);
    }
  }, [setFetchDetails, topbarCompanyId]);

  const getAttendanceCellValue = (row, dateKey) => {
    const dayData = row?.[dateKey];
    if (!dayData || typeof dayData !== "object") {
      return "-";
    }

    if (hoursViewType === "working_hours") {
      return (
        dayData.status_val_with_break_hours || dayData.attendance_status || "-"
      );
    }

    return (
      dayData.payable_grd_witn_ot_hours || dayData.attendance_status || "-"
    );
  };

  const getSummaryCellValue = (row, headerKey) => {
    if (headerKey === "total_duty_hours" && hoursViewType === "ot_hours") {
      return row?.total_payable_grd_shift_hours || "0";
    }

    if (headerKey === "total_duty_ot_break_hours") {
      if (hoursViewType === "ot_hours") {
        return row?.total_payable_grd_shift_ot_hours || "0";
      }

      if (hoursViewType === "working_hours") {
        return row?.total_duty_and_ot_hours || "0";
      }
    }

    return row?.[headerKey] || "0";
  };

  const tableSx = {
    minWidth:
      header1.reduce(
        (total, header) => total + getEmployeeColumnWidth(header.key),
        0,
      ) +
      dynamicHeaders.length * dynamicColumnWidth +
      visibleSummaryHeaders.length * summaryColumnWidth,
    tableLayout: "fixed",
    // "separate", not the default "collapse": collapsed borders belong to the
    // table grid, not the cell, so they stay behind when a sticky cell
    // moves and the frozen columns lost their vertical lines on scroll.
    borderCollapse: "separate",
    borderSpacing: 0,
  };

  return isLoading ? (
    <Loader info="Loading..." />
  ) : (
    <React.Fragment>
      <Mui.Box
        sx={{
          position: "relative",
          width: "100%",
          ...(SUPPORTS_SCROLL_TIMELINE ? { timelineScope: "--whx" } : {}),
        }}
      >
        <Mui.Box
          ref={headScrollRef}
          sx={{
            // Column-header strip is meaningless once rows become cards.
            display: { xs: "none", md: "block" },
            position: "sticky",
            top: APP_BAR_OFFSET + customFilterHeight,
            zIndex: 3,
            overflow: "hidden",
            width: "100%",
            backgroundColor: designColors.slate100,
          }}
        >
          <Mui.Table sx={tableSx}>
          <Mui.TableHead sx={designShell.tableStickyHead}>
            <Mui.TableRow sx={designShell.tableStickyHeadRow}>
              {/* approveTableHeadCell already carries a single
                  borderBottom (slate200), the same one-line-under-the-
                  header every other Reports screen uses. Every header cell
                  below used to also set a full 1px #d9d9d9 border on all
                  four sides, which drew a second, differently colored
                  border against the neighboring cell's own border — reading
                  as a visibly bolder/thicker header than Anomalies, Device
                  Status, etc. Removed everywhere in this head row; the
                  boxShadow that marks the frozen-column boundary (below)
                  is a separate, deliberate divider and is kept. */}
              {header1.map((header, index) => (
                <Mui.TableCell
                  key={header.key}
                  sx={{
                    ...approveTableHeadCell,
                    minWidth: getEmployeeColumnWidth(header.key),
                    width: getEmployeeColumnWidth(header.key),
                    maxWidth: getEmployeeColumnWidth(header.key),
                    position: "sticky",
                    left: header1LeftOffsets[header.key],
                    top: 0,
                    zIndex: 15,
                    backgroundColor: designColors.slate100,
                    whiteSpace: "nowrap",
                    borderRight: "1px solid #d9d9d9",
                    borderLeft: index === 0 ? "1px solid #d9d9d9" : 0,
                  }}
                >
                  {header.name}
                </Mui.TableCell>
              ))}
              {dynamicHeaders.map((header) => (
                <Mui.TableCell
                  key={header.date}
                  align="center"
                  sx={{
                    ...approveTableHeadCell,
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    ...scrollDrivenHeadCellSx,
                    backgroundColor: designColors.slate100,
                    borderRight: "1px solid #d9d9d9",
                    minWidth: dynamicColumnWidth,
                    width: dynamicColumnWidth,
                    whiteSpace: "nowrap",
                    wordBreak: "normal",
                    textAlign: "center",
                  }}
                >
                  <Mui.Box sx={{ textAlign: "center" }}>
                    <Mui.Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        lineHeight: 1.1,
                      }}
                    >
                      {header.day}
                    </Mui.Typography>
                    <Mui.Typography
                      variant="caption"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {header.day_number}
                    </Mui.Typography>
                  </Mui.Box>
                </Mui.TableCell>
              ))}
              {visibleSummaryHeaders.map((header) => (
                <Mui.TableCell
                  key={header.key}
                  sx={{
                    ...approveTableHeadCell,
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    ...scrollDrivenHeadCellSx,
                    backgroundColor: designColors.slate100,
                    borderRight: "1px solid #d9d9d9",
                    minWidth: summaryColumnWidth,
                    width: summaryColumnWidth,
                    whiteSpace: "normal",
                    wordBreak: isCompactHoursView ? "break-word" : "normal",
                    textAlign: "center",
                  }}
                >
                  <Mui.Box sx={{ textAlign: "center" }}>
                    {getSummaryHeaderLines(header).map((line, index) => (
                      <Mui.Typography
                        key={`${header.key}-${index}`}
                        sx={{
                          ...designType.tableHeaderCell,
                          lineHeight: 1.1,
                          whiteSpace: "nowrap",
                          color: "inherit",
                        }}
                      >
                        {line}
                      </Mui.Typography>
                    ))}
                  </Mui.Box>
                </Mui.TableCell>
              ))}
            </Mui.TableRow>
          </Mui.TableHead>
          </Mui.Table>
        </Mui.Box>
        <Mui.TableContainer
          ref={bodyScrollRef}
          sx={{
            width: "100%",
            overflowX: { xs: "visible", md: "auto" },
            overflowY: "hidden",
            overscrollBehaviorX: "contain",
            // Native bar hidden: the sticky proxy bar below replaces it.
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            ...(SUPPORTS_SCROLL_TIMELINE ? { scrollTimeline: "--whx x" } : {}),
          }}
        >
          <ApproveTable sx={tableSx}>
          <Mui.TableBody>
            {tableRows?.length === 0 ? (
              <TableEmptyState
                dataLength={masterLeaveDataForSearch?.attendance_data}
                colSpan={
                  header1.length +
                  dynamicHeaders.length +
                  visibleSummaryHeaders.length
                }
              />
            ) : (
              tableRows
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((employee, index) => (
                  <Mui.TableRow
                    key={`${employee.user_id || employee.employee_id}-${index}`}
                    sx={{
                      ...designShell.tableRowHover,
                      "& td": {
                        backgroundColor: designColors.white,
                      },
                    }}
                  >
                    {header1.map((header, headerIndex) => (
                      <Mui.TableCell
                        key={`${employee.user_id || employee.employee_id}-${header.key}`}
                        data-label={header.name}
                        sx={{
                          ...approveTableBodyCell,
                          // approveTableBodyCell forces nowrap + ellipsis by
                          // default — overridden below so the full value
                          // always renders (wrapping to a second line rather
                          // than hiding behind a hover-only Tooltip, which
                          // wasn't an acceptable fix).
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflow: "visible",
                          textOverflow: "clip",
                          minWidth: getEmployeeColumnWidth(header.key),
                          width: getEmployeeColumnWidth(header.key),
                          maxWidth: getEmployeeColumnWidth(header.key),
                          borderRight: "1px solid #d9d9d9",
                          borderBottom: "1px solid #d9d9d9",
                          ...(headerIndex === 0 ? { borderLeft: "1px solid #d9d9d9" } : {}),
                          position: "sticky",
                          left: header1LeftOffsets[header.key],
                          // Must stay BELOW the sticky header group (3) and the
                          // sticky filter bar (4). At 9 these frozen body cells
                          // painted over the header while scrolling down.
                          zIndex: 1,
                          backgroundColor: designColors.white,
                        }}
                      >
                        {employee[header.key] || "-"}
                      </Mui.TableCell>
                    ))}
                    {dynamicHeaders.map((header) => {
                      const attendanceValue = getAttendanceCellValue(
                        employee,
                        header.date,
                      );
                      return (
                        <Mui.TableCell
                          key={`${employee.user_id || employee.employee_id}-${header.date}`}
                          data-label={`${header.day || ""} ${header.day_number || ""}`.trim()}
                          align="center"
                          sx={{
                            ...approveTableBodyCell,
                            minWidth: dynamicColumnWidth,
                            width: dynamicColumnWidth,
                            // Values like "P | 08:30" routinely overflow this
                            // column's compact fixed width — wrap instead of
                            // ellipsis-clipping so the full value is always
                            // visible without needing to hover.
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflow: "visible",
                            textOverflow: "clip",
                            borderRight: "1px solid #d9d9d9",
                            borderBottom: "1px solid #d9d9d9",
                          }}
                        >
                          {attendanceValue}
                        </Mui.TableCell>
                      );
                    })}
                    {visibleSummaryHeaders.map((header) => {
                      const summaryValue = getSummaryCellValue(
                        employee,
                        header.key,
                      );
                      return (
                        <Mui.TableCell
                          key={`${employee.user_id || employee.employee_id}-${header.key}`}
                          data-label={getSummaryHeaderLines(header).join(" ")}
                          align="center"
                          sx={{
                            ...approveTableBodyCell,
                            textAlign: "center",
                            minWidth: summaryColumnWidth,
                            width: summaryColumnWidth,
                            // Always wrap rather than ellipsis-clip, so the
                            // full value is always visible without a hover.
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflow: "visible",
                            textOverflow: "clip",
                            borderRight: "1px solid #d9d9d9",
                            borderBottom: "1px solid #d9d9d9",
                          }}
                        >
                          {summaryValue}
                        </Mui.TableCell>
                      );
                    })}
                  </Mui.TableRow>
                ))
            )}
          </Mui.TableBody>
          </ApproveTable>
        </Mui.TableContainer>
        <Mui.Box
          ref={proxyScrollRef}
          sx={{
            position: "sticky",
            // The footer bar sticks at bottom:-1, so overlap by that 1px to leave no gap.
            bottom: Math.max(0, footerHeight - 1),
            zIndex: 4,
            overflowX: "auto",
            overflowY: "hidden",
            width: "100%",
            display: "none",
            backgroundColor: designColors.white,
          }}
        >
          <Mui.Box ref={proxyInnerRef} sx={{ height: "1px" }} />
        </Mui.Box>
      </Mui.Box>
      <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
        {paginationFooter({
          page,
          rowsPerPage,
          total: tableRows?.length || 0,
          itemLabel: "employees",
          onPageChange: setPage,
          onRowsPerPageChange: handleChangeRowsPerPage,
          rowsPerPageOptions,
        })}
      </Mui.Box>
    </React.Fragment>
  );
};
